import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import GalleryModal from '../components/GalleryModal'
import GoogleMap from '../components/GoogleMap'
import { getResizedImageUrl } from '../lib/utils'

export default function Referencer() {
  const [leveringerGallery, setLeveringerGallery] = useState(null)
  const [virksomhedenGallery, setVirksomhedenGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Lightbox State
  const [lightboxImages, setLightboxImages] = useState([])
  const [activeImageIndex, setActiveImageIndex] = useState(-1)

  useEffect(() => {
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/by-slug/da/leveringer').then((res) => res.json()),
      fetch('https://api.dev.auxo.dk/public/by-slug/da/virksomheden').then((res) => res.json())
    ])
      .then(([leveringer, virksomheden]) => {
        setLeveringerGallery(leveringer)
        setVirksomhedenGallery(virksomheden)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af gallerier:', err)
        setLoading(false)
      })
  }, [])

  const openLightbox = (images, index) => {
    setLightboxImages(images)
    setActiveImageIndex(index)
  }

  return (
    <>
      <Helmet>
        <title>Referencer | Recars ApS</title>
        <meta name="description" content="Vores senest udførte arbejde Vi udfører en bred vifte af opgaver – se vores seneste referencer her." />
        <meta property="og:title" content="Referencer | Recars ApS" />
        <meta property="og:description" content="Vores senest udførte arbejde Vi udfører en bred vifte af opgaver – se vores seneste referencer her." />
        <link rel="canonical" href="https://recars.dk/referencer/" />
      </Helmet>

      {/* Header */}
      <section className="relative bg-dark-950 pt-32 sm:pt-40 lg:pt-44 pb-12 border-b border-dark-800">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12 text-left">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#c5a880]">
            Vores arbejde
          </span>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Referencer
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-slate-400">
            Lad os stå for din næste opgave. Her kan du få et indblik i, hvad vi kan hjælpe dig med på dette område.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-dark-950 py-20">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12">
          {/* Scraped Contact Block */}
          <div className="rounded-2xl border border-dark-800 bg-dark-900 p-8 text-center max-w-2xl mx-auto mb-16">
            <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider mb-4">
              Lad os stå for din næste opgave
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-6">
              <a
                href="tel:+4571999177"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#c5a880] hover:text-[#b39368] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" /> +45 71 99 91 77
              </a>
              <span className="hidden sm:inline text-slate-700">|</span>
              <a
                href="mailto:info@recars.dk"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#c5a880] hover:text-[#b39368] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope" /> info@recars.dk
              </a>
            </div>
          </div>

          {/* Reference Card Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-2xl border border-dark-800 bg-dark-900 p-2 shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/placeholder.jpg"
                  alt="Reference placeholder"
                  className="w-full h-auto rounded-xl object-cover aspect-square"
                />
              </div>
            </div>
            <div className="lg:col-span-7 text-left flex flex-col gap-6">
              <h2 className="text-2xl font-bold text-white sm:text-3xl">
                Vores senest udførte arbejde
              </h2>
              <p className="text-sm leading-relaxed text-slate-300">
                Vi udfører en bred vifte af opgaver – se vores seneste referencer her. Vi leverer altid skræddersyede løsninger med udgangspunkt i dine ønsker.
              </p>
              <div>
                <Link
                  to="/reference"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#c5a880] px-5 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-[#b39368] transition-all duration-300"
                >
                  Læs mere om vores projekter <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>

          {/* CMS Galleries */}
          {!loading && (
            <div className="space-y-20 border-t border-dark-800 pt-16">
              {/* Deliveries Gallery */}
              {leveringerGallery && leveringerGallery.billeder?.length > 0 && (
                <div className="text-left">
                  <h3 className="font-display text-xl font-bold text-white uppercase tracking-wider mb-2">
                    {leveringerGallery.titel?.da || leveringerGallery.titel}
                  </h3>
                  <p className="text-xs text-slate-400 mb-6">
                    {leveringerGallery.beskrivelse?.da || leveringerGallery.beskrivelse}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {leveringerGallery.billeder.map((img, idx) => (
                      <div
                        key={idx}
                        className="group relative overflow-hidden rounded-xl border border-dark-800 bg-dark-900 aspect-video cursor-zoom-in shadow-md hover:border-dark-700 transition-all"
                        onClick={() => openLightbox(leveringerGallery.billeder, idx)}
                      >
                        <img
                          src={getResizedImageUrl(img, 400)}
                          alt={`Levering ${idx + 1}`}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Virksomheden Galleri */}
              {virksomhedenGallery && virksomhedenGallery.billeder?.length > 0 && (
                <div className="text-left">
                  <h3 className="font-display text-xl font-bold text-white uppercase tracking-wider mb-2">
                    {virksomhedenGallery.titel?.da || virksomhedenGallery.titel}
                  </h3>
                  <p className="text-xs text-slate-400 mb-6">
                    {virksomhedenGallery.beskrivelse?.da || virksomhedenGallery.beskrivelse}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {virksomhedenGallery.billeder.map((img, idx) => (
                      <div
                        key={idx}
                        className="group relative overflow-hidden rounded-xl border border-dark-800 bg-dark-900 aspect-video cursor-zoom-in shadow-md hover:border-dark-700 transition-all"
                        onClick={() => openLightbox(virksomhedenGallery.billeder, idx)}
                      >
                        <img
                          src={getResizedImageUrl(img, 400)}
                          alt={`Virksomheden ${idx + 1}`}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Google Maps Section */}
      <GoogleMap />

      {/* Lightbox Modal */}
      {activeImageIndex >= 0 && (
        <GalleryModal
          images={lightboxImages}
          currentIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(-1)}
          onPrev={() => setActiveImageIndex((prev) => (prev === 0 ? lightboxImages.length - 1 : prev - 1))}
          onNext={() => setActiveImageIndex((prev) => (prev === lightboxImages.length - 1 ? 0 : prev + 1))}
        />
      )}
    </>
  )
}
